<template>
  <div class="view-case">
    <div class="case-container">
      <div v-if="loading" class="loader" />
      <div v-else>
        <ReturnCaseSection
          v-if="isReturn"
          :is-edit-mode="caseNotSubmitted"
          :return-case="returnCase"
          @form-is-valid="updateCaseValidity"
          @update-case="onUpdateCase" />

        <WarrantyCaseSection
          v-if="isWarranty"
          :is-edit-mode="caseNotSubmitted"
          :warranty-case="caseOrder"
          @form-is-valid="updateCaseValidity"
          @delete-case-line="onDeleteLine"
          @update-case="onUpdateCase" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CaseType } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ReturnCaseSection from '@/components/cases/ReturnCaseSection.vue';
import WarrantyCaseSection from '@/components/cases/WarrantyCaseSection.vue';
import { useCaseManagement } from '@/composables/useCaseManagement';
import { useCaseValidation } from '@/composables/useCaseValidation';
import { useNotification } from '@/composables/useNotification';
import type { CaseLine } from '@/models/case-line.model';
import { useCaseStore } from '@/stores/case';
import { useProductMatrixStore } from '@/stores/product-matrix';
import { useUserStore } from '@/stores/user';
import type { Case, ReturnCase } from '@/types/case.interface';

const props = defineProps<{
  caseId?: string;
  caseType: CaseType;
}>();

const emit = defineEmits<{
  'case-is-valid': [isValid: boolean];
}>();

const userStore = useUserStore();
const caseStore = useCaseStore();
const productMatrixStore = useProductMatrixStore();

const { t } = useI18n();
const { notifyError } = useNotification();
const { fetchCase, navigateBackToCases } = useCaseManagement(props);
const { isReturn, isWarranty, caseNotSubmitted } = useCaseValidation();

const { selectedAccount: account } = storeToRefs(userStore);

const loading = ref(true);

const caseOrder = computed(() => {
  return (caseStore.updatedCaseOrder ?? caseStore.caseOrder) as Case;
});

// casting in template breaks IDE theme
const returnCase = computed(() => {
  return caseOrder.value as ReturnCase;
});

const onDeleteLine = (line: CaseLine) => {
  const updatedLines = caseOrder.value.lines.filter((l) => l.id !== line.id);

  caseStore.setUpdatedCase({ ...caseOrder.value, lines: updatedLines });

  const matrixProduct = productMatrixStore.activeMatrixOrder?.matrixProducts?.[line.productId];
  productMatrixStore.updateMatrixOrder({
    ...productMatrixStore.activeMatrixOrder,
    matrixProducts: {
      ...productMatrixStore.activeMatrixOrder.matrixProducts,
      [line.productId]: {
        ...matrixProduct,
        quantity: (matrixProduct?.quantity || 0) - 1,
      },
    },
  });
};

const onUpdateCase = (updatedCase: Case) => {
  caseStore.setUpdatedCase(updatedCase);
};

onMounted(() => {
  init();
});

const init = async () => {
  loading.value = true;

  try {
    if (caseOrder.value) {
      return;
    }

    if (props.caseId) {
      await fetchCase(props.caseId, account.value.id);
    }
  } catch (e) {
    notifyError(t('global.error'));
    navigateBackToCases();
  } finally {
    loading.value = false;
  }
};

const updateCaseValidity = (isValid: boolean) => {
  emit('case-is-valid', isValid);
};
</script>
<style scoped lang="scss">
.view-case {
  @include common-container;
  max-width: 1660px;
  padding: 0 20px;
  margin-top: 1.5rem;

  .case-container {
    background-color: var(--bg-page-color);
  }
}
</style>
